// umlaut-check: skip — technische Schlüssel der Bestandskennzahlen (JSON-Schnittstelle).
import React, {useCallback, useState} from 'react';
import OeTip from './OeTip';
import BestandTooltip from '../Stock/BestandTooltip';
import {bookStock, createProduction} from './api';
import {fmt0} from './format';
import {WH_LAHR, hasProductionArticles} from './wizard';

// Schritt 5 des geführten Ablaufs: Produktion. Original `renderProdBar`
// (2301–2403), `askSetCorrect`/`doSetCorrect` (2426), `askCompOut`/`doCompOut`
// (2447), `bookComponent` (2465), `prodStatusLabel` (2507), `openProdModal`
// (2519) und `doCreateProduction` (4890).
// ABGLEICH E76–E82, F2–F4, G21–G24, A22, A23.
//
// Produziert wird IMMER in Lahr — unabhängig vom Auftragslager. Bestände und
// Korrekturbuchungen der Komponenten laufen deshalb grundsätzlich gegen Lahr.

/** Statusnamen der Produktionsaufträge (Original `prodStatusLabel`, 2507). */
const PROD_STATUS = {
    ENTRY_IN_PROGRESS: 'in Erfassung',
    RELEASED: 'freigegeben',
    IN_PRODUCTION: 'in Produktion',
    PRODUCED: 'produziert',
    CLOSED: 'abgeschlossen',
    CANCELLED: 'storniert',
};

function prodStatusLabel(s) {
    return PROD_STATUS[s] || s || '?';
}

export default function StepProduktion({
    ctx,
    wizard,
    orderRef,
    toast,
    ask,
    askQty,
    abilities,
    settings,
    onReloadProdPlan,
    onRefreshStock,
}) {
    const {order, stock} = ctx;
    const [busy, setBusy] = useState(false);
    // Ergebnis der letzten Anlage — bleibt stehen, bis der Editor neu lädt (Auftrag #548).
    const [lastCreated, setLastCreated] = useState(null);
    const canProduce = !!(abilities && abilities.production);
    const canWarehouse = !!(abilities && abilities.warehouse);
    const lahrGesperrt = settings && settings.lahrStockTrusted === false;

    /**
     * Komponenten-Bestand korrigieren (Original `bookComponent`, 2465): immer
     * gegen das Lahr-Lager, danach Plan und Bestand frisch ziehen.
     */
    const bookComponent = useCallback(async (articleId, articleNumber, qty, direction) => {
        if (!articleId || !(qty > 0)) {
            return;
        }
        const out = direction === 'out';
        setBusy(true);
        toast(`${articleNumber}: bucht ${fmt0(qty)} ${out ? 'aus' : 'ein'} …`);
        try {
            const j = await bookStock(orderRef, {
                warehouseId: WH_LAHR,
                items: [{articleId: String(articleId), articleNumber, quantity: qty, direction: out ? 'out' : 'in'}],
            });
            if (j.dryRun) {
                toast(`Trockenlauf: ${j.message || 'Es wurde nichts gebucht.'}`);
                return;
            }
            if (!j.ok) {
                toast('Buchung fehlgeschlagen: '
                    + (j.error || (j.failed && j.failed[0] && j.failed[0].error) || 'unbekannt'),
                {variant: 'danger', autohide: false});
                return;
            }
            toast(`✓ ${articleNumber}: ${out ? '−' : '+'}${fmt0(qty)} in Lahr gebucht — wird aktualisiert …`);
            await onReloadProdPlan(true);
            await onRefreshStock();
            toast(`✓ ${articleNumber}: Bestand aktualisiert`);
        } catch (e) {
            toast('Buchung fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                {variant: 'danger', autohide: false});
        } finally {
            setBusy(false);
        }
    }, [orderRef, toast, onReloadProdPlan, onRefreshStock]);

    /** Gezählten Ist-Bestand fertiger Sets eintragen (Original `askSetCorrect`, 2403). */
    const askSetCorrect = useCallback((p) => {
        const cur = Math.max(0, Math.floor(p.setStock || 0));
        const frei = Math.max(0, Math.floor(p.setFree != null ? p.setFree : (p.setStock || 0)));
        const minVal = cur - frei;

        askQty({
            title: 'Fertige Sets am Lager korrigieren',
            confirmLabel: 'Bestand korrigieren',
            tone: 'danger',
            hint: (
                <>
                    Trage den <b>gezählten Ist-Bestand</b> fertiger <b>{p.articleNumber}</b> im Lager <b>Lahr</b>{' '}
                    ein. Gebucht wird die Differenz per Waren-Ein-/Ausgang ohne Bezug (echte Lagerbewegung,
                    erscheint im Wochenreport).
                    {frei < cur ? <> <b>Runter höchstens auf {fmt0(minVal)}</b> — der Rest ist für andere Aufträge
                        reserviert.</> : null}
                </>
            ),
            item: <><b>{p.articleNumber}</b> · {p.name || ''}</>,
            label: `aktuell ${fmt0(cur)} →`,
            inputTip: 'Gezählter Ist-Bestand fertiger Sets in Lahr.',
            value: cur,
            min: minVal,
            max: 999999,
            preview: (q) => (q === cur ? '(unverändert)' : `(Differenz ${q > cur ? '+' : '−'}${fmt0(Math.abs(q - cur))})`),
            onConfirm: (ziel) => {
                const delta = Math.max(minVal, ziel) - cur;
                if (delta === 0) {
                    toast('Bestand unverändert');
                    return;
                }
                bookComponent(p.articleId, p.articleNumber, Math.abs(delta), delta > 0 ? 'in' : 'out');
            },
        });
    }, [askQty, toast, bookComponent]);

    /** Rückfrage vor dem Senken einer Komponente (Original `askCompOut`, 2436). */
    const askCompOut = useCallback((b, frei) => {
        ask({
            title: 'Komponente senken',
            confirmLabel: `${fmt0(frei)} ausbuchen`,
            tone: 'danger',
            body: (
                <>
                    <p className="weg">
                        ⚠ Bucht <b>{fmt0(frei)}</b> Stück per Warenausgang ohne Bezug aus dem Lager <b>Lahr</b> aus.
                    </p>
                    <p>
                        Echte Lagerbewegung (erscheint im Wochenreport). Gesenkt wird nur der <b>verfügbare</b> Anteil —
                        die <b>von anderen Aufträgen reservierte Menge bleibt gedeckt</b>. Nur bestätigen, wenn die Ware physisch
                        wirklich nicht da ist.
                    </p>
                    <p className="neu"><b>{b.articleNumber}</b> · {b.name || ''}</p>
                    <p className="muted">Auszubuchen: {fmt0(frei)} Stück (verfügbarer Lahr-Bestand)</p>
                </>
            ),
            onConfirm: () => bookComponent(b.articleId, b.articleNumber, frei, 'out'),
        });
    }, [ask, bookComponent]);

    /** Produktionsaufträge anlegen (Original `openProdModal` 2507, `doCreateProduction` 4890). */
    const openProdModal = useCallback((onlyArticleId) => {
        const plan = wizard.prodPlan;
        const items = ((plan && plan.toProduce) || [])
            .filter((p) => p.need > 0 && !(p.existing || []).length)
            .filter((p) => !onlyArticleId || String(p.articleId) === String(onlyArticleId))
            .map((p) => ({
                articleId: String(p.articleId),
                salesOrderItemId: p.salesOrderItemId != null ? String(p.salesOrderItemId) : null,
                articleNumber: p.articleNumber,
                title: p.name,
                quantity: p.need,
                canProduce: p.canProduce,
                openUnlinked: p.openUnlinked || [],
            }));

        if (!items.length) {
            return;
        }

        ask({
            title: 'Produktionsaufträge anlegen',
            confirmLabel: 'Jetzt anlegen',
            tone: 'danger',
            body: (
                <>
                    {items.some((i) => !i.canProduce) ? (
                        <p className="chg">
                            ⚠ Für mindestens einen Artikel fehlen laut Bestand die Komponenten — weclapp legt den
                            Produktionsauftrag trotzdem an.
                        </p>
                    ) : null}
                    {items.some((i) => i.openUnlinked.length) ? (
                        <p className="chg">
                            ⚠ Für {items.filter((i) => i.openUnlinked.length).map((i) => i.articleNumber).join(', ')} gibt es
                            schon einen offenen Produktionsauftrag ohne Auftragsbezug
                            ({items.flatMap((i) => i.openUnlinked).map((o) => o.number).join(', ')}). Bitte erst in weclapp
                            prüfen, ob er zu diesem Auftrag gehört.
                        </p>
                    ) : null}
                    <p>
                        Legt in weclapp je Artikel einen Produktionsauftrag an (auftragsbezogen).{' '}
                        <b>Nur Anlegen</b> — das Fertigmelden/Einbuchen erfolgt danach in weclapp (per API nicht
                        möglich).
                    </p>
                    <p className="neu">
                        <b>{items.length} Produktionsauftrag/-aufträge</b> werden angelegt:
                    </p>
                    <div className="scrolllist">
                        {items.map((i) => (
                            <div key={i.salesOrderItemId || i.articleId}>
                                {i.articleNumber} · {i.title || ''} — Menge {fmt0(i.quantity)}
                                {i.canProduce ? '' : <span className="bom-no"> (Komponenten knapp)</span>}
                            </div>
                        ))}
                    </div>
                </>
            ),
            onConfirm: async () => {
                setBusy(true);
                toast('Produktionsaufträge werden angelegt…');
                try {
                    const j = await createProduction(orderRef, items.map((i) => ({
                        articleId: i.articleId,
                        salesOrderItemId: i.salesOrderItemId || undefined,
                        articleNumber: i.articleNumber,
                        title: i.title,
                        quantity: i.quantity,
                    })));
                    if (j.dryRun) {
                        setLastCreated({created: j.created || [], problems: [...(j.blocked || []), ...(j.failed || [])], dryRun: true});
                        toast(`Trockenlauf: ${j.message || 'Es wurde kein Produktionsauftrag angelegt.'}`);
                        return;
                    }
                    const okN = (j.created || []).length;
                    const failN = (j.failed || []).length + (j.blocked || []).length;
                    if (okN) {
                        setLastCreated({created: j.created || [], problems: [...(j.blocked || []), ...(j.failed || [])]});
                        toast(`✓ ${okN} Produktionsauftrag/-aufträge angelegt`
                            + (failN ? ` · ${failN} nicht angelegt` : '')
                            + ' · Links stehen im Kasten unten');
                        await onReloadProdPlan(true);
                        return;
                    }
                    toast('Fehler: ' + ((j.failed && j.failed[0] && j.failed[0].error) || j.error || 'unbekannt'),
                        {variant: 'danger', autohide: false});
                } catch (e) {
                    const d = e.response?.data || {};
                    const grund = (d.blocked && d.blocked[0] && d.blocked[0].error)
                        || (d.failed && d.failed[0] && d.failed[0].error) || d.error || e.message;
                    toast('Nicht angelegt: ' + grund, {variant: 'danger', autohide: false});
                    if (d.blocked && d.blocked.length) {
                        await onReloadProdPlan(true);
                    }
                } finally {
                    setBusy(false);
                }
            },
        });
    }, [wizard.prodPlan, ask, orderRef, toast, onReloadProdPlan]);

    // ── Darstellung ─────────────────────────────────────────────────────────

    const keineProduktion = (
        <div className="fbar" style={{display: 'flex'}}>
            <span className="lbl">Produktion:</span>
            <span className="poinfo" style={{color: 'var(--ok)', display: 'inline'}}>
                ✓ Keine Artikel, die produziert werden müssen — einfach weiter.
            </span>
        </div>
    );

    if (stock.loaded && !hasProductionArticles(ctx)) {
        return keineProduktion;
    }

    const plan = wizard.prodPlan;
    if (plan === null) {
        return (
            <div className="fbar" style={{display: 'flex'}}>
                <span className="muted">⚙ Produktionsdaten werden geladen…</span>
            </div>
        );
    }

    const tp = plan.toProduce || [];
    const ex = plan.existingOrders || [];
    if (!tp.length && !ex.length) {
        return keineProduktion;
    }

    const orderWh = String(plan.orderWarehouse || '');
    const producible = tp.filter((p) => p.need > 0 && !(p.existing || []).length);

    // Produktionsaufträge je Artikelnummer, damit jede Karte ihren eigenen
    // Absprung nach weclapp zeigt (Original 2330).
    const paByArt = {};
    const paSeen = new Set();
    ex.forEach((o) => {
        (paByArt[o.articleNumber] = paByArt[o.articleNumber] || []).push(o);
    });

    return (
        <div className="fbar" style={{display: 'flex'}}>
            <div style={{width: '100%'}}>
                <div className="lbl" style={{marginBottom: 8}}>
                    ⚙ Produktion — zu fertigende Artikel &amp; Stücklisten
                    <OeTip tip={'Der Lagermitarbeiter sieht hier, welche Sets er produzieren muss und ob die Komponenten dafür da sind. „Erzeugen" legt nur die technischen Produktionsaufträge in weclapp an.'}>
                        <span className="poinfo muted" style={{display: 'inline', marginLeft: 6}}>?</span>
                    </OeTip>
                </div>

                {plan.existingOrdersError ? (
                    <div className="prodwarn">⚠ {plan.existingOrdersError}</div>
                ) : null}

                {lastCreated ? (
                    <div className="prodcreated">
                        <div className="lbl" style={{marginBottom: 4}}>
                            {lastCreated.dryRun
                                ? 'Trockenlauf — so würden die Produktionsaufträge angelegt (in weclapp entsteht nichts):'
                                : '✓ Angelegt — jetzt in weclapp fertigmelden und einbuchen:'}
                        </div>
                        {lastCreated.created.map((c) => (
                            <div key={c.id || c.articleNumber}>
                                <b>{c.number || '?'}</b> · {c.articleNumber}{c.title ? ` · ${c.title}` : ''} — Menge {fmt0(c.quantity)}{' '}
                                {c.webUrl ? (
                                    <OeTip tip="Öffnet den Produktionsauftrag in weclapp in einem neuen Tab.">
                                        <a className="doclink" href={c.webUrl} target="_blank" rel="noopener noreferrer">
                                            in weclapp öffnen ↗
                                        </a>
                                    </OeTip>
                                ) : null}
                            </div>
                        ))}
                        {lastCreated.problems.map((f, i) => (
                            <div key={`f${i}`} className="bom-no">✕ {f.articleNumber}: {f.error}</div>
                        ))}
                    </div>
                ) : null}

                {orderWh && orderWh !== WH_LAHR && producible.length ? (
                    <div className="prodwarn">
                        ⚙ <b>Produziert wird immer ab Lahr, für Lahr.</b> Komponenten-Bestände und
                        Korrekturbuchungen beziehen sich auf <b>Lahr</b>, der Produktionsauftrag wird in{' '}
                        <b>Lahr</b> angelegt. <b>Kein Umlagern, nichts mit Schneider</b> — einfach erzeugen und in
                        weclapp fertigmelden.
                    </div>
                ) : null}

                {tp.length ? (
                    <div className="prodcards">
                        {tp.map((p) => {
                            const pas = p.existing ? p.existing : (paByArt[p.articleNumber] || []);
                            const gesperrt = !!(p.existing && p.existing.length);
                            pas.forEach((o) => paSeen.add(o.id));
                            const setFree = Math.max(0, Math.floor(p.setFree != null ? p.setFree : (p.setStock || 0)));

                            return (
                                <div className="prodcard" key={p.salesOrderItemId || p.articleId}>
                                    <div className="pc-head">
                                        <div className="pc-title">
                                            <b>{p.articleNumber} · {p.name || ''}</b>
                                            {p.need > 0
                                                ? <> — herstellen: <b>{fmt0(p.need)}</b></>
                                                : <span className="muted"> — nichts offen</span>}
                                        </div>
                                        <div className="pc-pills">
                                            <OeTip tip={p.canProduce
                                                ? 'Alle Komponenten der Stückliste sind laut Lahr-Bestand in ausreichender Menge da.'
                                                : 'Mindestens eine Komponente reicht laut Lahr-Bestand nicht. Der Produktionsauftrag lässt sich trotzdem anlegen.'}>
                                                <span className={p.canProduce ? 'bom-ok' : 'bom-no'}>
                                                    {p.canProduce ? '✓ Komponenten da' : '✕ Komponenten fehlen'}
                                                </span>
                                            </OeTip>
                                            {pas.map((o) => (
                                                <OeTip key={o.id} tip="Öffnet den Produktionsauftrag in weclapp — dort fertigmelden und einbuchen.">
                                                    <a
                                                        className="pa-pill"
                                                        href={o.webUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {o.number} · {prodStatusLabel(o.status)} ↗
                                                    </a>
                                                </OeTip>
                                            ))}
                                            {gesperrt ? null : (p.openUnlinked || []).map((o) => (
                                                <OeTip key={`u${o.id}`} tip={`Offener Produktionsauftrag für diesen Artikel ohne Bezug zu einem Auftrag (${fmt0(o.quantity)} Stück, ${prodStatusLabel(o.status)}). Er kann zu diesem oder einem anderen Auftrag gehören — vor dem Erzeugen in weclapp prüfen.`}>
                                                    <a
                                                        className="pa-pill"
                                                        href={o.webUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {o.number} · ohne Auftragsbezug ↗
                                                    </a>
                                                </OeTip>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="pc-set">
                                        <BestandTooltip
                                            werte={{
                                                lagerbestand: p.setStock || 0,
                                                reserviertFremd: p.setReserved || 0,
                                                verfuegbar: p.setFree != null ? p.setFree : (p.setStock || 0),
                                            }}
                                            lagerName="Lahr Hauptlager"
                                            haelterUrl={`/order-editor/${encodeURIComponent(orderRef)}/reservations/${encodeURIComponent(p.articleId)}`}
                                            haelterParams={{warehouse: WH_LAHR}}
                                        >
                                            <span tabIndex={0}>
                                                <i className="dot"/> Fertige Sets am Lager (Lahr): <b>{fmt0(p.setStock || 0)}</b>
                                                {setFree > 0 ? <span className="muted"> (verfügbar {fmt0(setFree)})</span> : null}
                                            </span>
                                        </BestandTooltip>
                                        <OeTip tip={lahrGesperrt
                                            ? 'Lahr-Bestand in Pflege — Reservierungen erst nach Freigabe'
                                            : 'Gezählten Ist-Bestand fertiger Sets in Lahr eintragen — bucht die Differenz per Waren-Ein-/Ausgang ohne Bezug. Echte Lagerbewegung, erscheint im Wochenreport. Im Testmodus läuft das als Trockenlauf.'}>
                                            <button
                                                type="button"
                                                className="stkbtn"
                                                disabled={busy || !canWarehouse || lahrGesperrt}
                                                onClick={() => askSetCorrect(p)}
                                            >
                                                Bestand korrigieren
                                            </button>
                                        </OeTip>
                                    </div>

                                    <div className="pc-bom">
                                        {(p.bom || []).length ? (p.bom || []).map((b) => {
                                            const compFree = Math.floor(b.free != null ? b.free : b.stock);
                                            return (
                                                <div className="bomrow" key={b.articleId}>
                                                    <span className="bom-art">
                                                        {b.articleNumber} · {b.name || ''}{' '}
                                                        <span className="muted">({fmt0(b.perUnit)}×)</span>
                                                    </span>
                                                    <span className="bom-stock">
                                                        <BestandTooltip
                                                            werte={{
                                                                lagerbestand: b.stock,
                                                                reserviertFremd: b.reserved || 0,
                                                                verfuegbar: b.free != null ? b.free : b.stock,
                                                            }}
                                                            lagerName="Lahr Hauptlager"
                                                            haelterUrl={`/order-editor/${encodeURIComponent(orderRef)}/reservations/${encodeURIComponent(b.articleId)}`}
                                                            haelterParams={{warehouse: WH_LAHR}}
                                                        >
                                                            <span tabIndex={0}>
                                                                <span className={b.enough ? 'bom-ok' : 'bom-no'}>
                                                                    {b.enough ? '✓' : '✕'} {fmt0(b.stock)} / {fmt0(b.required)} nötig
                                                                </span>
                                                                {b.free != null && b.free !== b.stock ? (
                                                                    <span className="muted"> (verfügbar {fmt0(Math.max(0, b.free))})</span>
                                                                ) : null}
                                                            </span>
                                                        </BestandTooltip>
                                                        {!b.enough && b.missing > 0 ? (
                                                            <OeTip tip={lahrGesperrt
                                                                ? 'Lahr-Bestand in Pflege — Reservierungen erst nach Freigabe'
                                                                : `Bucht die Fehlmenge (${fmt0(b.missing)}) per Wareneingang ohne Bezug ins Lahr-Lager — echte Bestandskorrektur, damit produziert werden kann. Im Testmodus läuft das als Trockenlauf.`}>
                                                                <button
                                                                    type="button"
                                                                    className="stkbtn ok"
                                                                    disabled={busy || !canWarehouse || lahrGesperrt}
                                                                    onClick={() => bookComponent(b.articleId, b.articleNumber, b.missing, 'in')}
                                                                >
                                                                    ✓ doch da
                                                                </button>
                                                            </OeTip>
                                                        ) : null}
                                                        {compFree > 0 ? (
                                                            <OeTip
                                                                tip={lahrGesperrt
                                                                    ? 'Lahr-Bestand in Pflege — Reservierungen erst nach Freigabe'
                                                                    : `Liegt physisch nicht im Lager: ${fmt0(compFree)} Stück per Warenausgang ohne Bezug aus Lahr senken.`}
                                                                detail={lahrGesperrt
                                                                    ? undefined
                                                                    : 'Gesenkt wird nur der verfügbare Anteil — von anderen Aufträgen reservierte Mengen bleiben gedeckt. Fragt vorher nach; im Testmodus Trockenlauf.'}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="stkbtn no"
                                                                    disabled={busy || !canWarehouse || lahrGesperrt}
                                                                    onClick={() => askCompOut(b, compFree)}
                                                                >
                                                                    ✕ nicht da
                                                                </button>
                                                            </OeTip>
                                                        ) : null}
                                                    </span>
                                                </div>
                                            );
                                        }) : (
                                            <span className="muted">keine Stückliste in weclapp hinterlegt</span>
                                        )}
                                    </div>

                                    {p.need > 0 ? (
                                        <div className="pc-actions">
                                            <OeTip tip={gesperrt
                                                ? `Für diese Position gibt es schon den Produktionsauftrag ${pas.map((o) => `${o.number} (${prodStatusLabel(o.status)})`).join(', ')}. Ein zweiter würde doppelt produzieren — den vorhandenen öffnest du über den Link oben rechts auf der Karte.`
                                                : canProduce
                                                    ? 'Legt für diesen Artikel einen auftragsbezogenen Produktionsauftrag in weclapp an. Nur Anlegen — fertigmelden und einbuchen geht danach in weclapp. Im Testmodus läuft das als Trockenlauf.'
                                                    : 'Zum Anlegen von Produktionsaufträgen fehlt das Recht „Produktion".'}>
                                                <button
                                                    type="button"
                                                    className="actbtn sm"
                                                    disabled={busy || !canProduce || gesperrt}
                                                    onClick={() => openProdModal(p.articleId)}
                                                >
                                                    {gesperrt ? '✓ Produktionsauftrag vorhanden' : '⚙ Produktionsauftrag erzeugen'}
                                                </button>
                                            </OeTip>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="muted">Keine offenen Produktionsartikel.</div>
                )}

                <div className="prodhelp">
                    💡 Der Lagermitarbeiter erkennt, welche Sets er <b>produzieren</b> muss, und vermerkt auf dem
                    Kommissionierschein, ob es geklappt hat. „Erzeugen" legt die technischen Produktionsaufträge in
                    weclapp an — <b>fertigmelden/einbuchen passiert in weclapp</b> (per API nicht möglich). Erst
                    danach zum Bestand, sonst entstehen Fehlmengen.
                </div>

                {producible.length > 1 ? (
                    <div className="prodfoot">
                        <OeTip tip={canProduce
                            ? `Legt für alle ${producible.length} offenen Produktionsartikel je einen Produktionsauftrag in weclapp an. Nur Anlegen — fertigmelden geht in weclapp. Im Testmodus läuft das als Trockenlauf.`
                            : 'Zum Anlegen von Produktionsaufträgen fehlt das Recht „Produktion".'}>
                            <button
                                type="button"
                                className="actbtn"
                                disabled={busy || !canProduce}
                                onClick={() => openProdModal(null)}
                            >
                                ⚙ Alle erzeugen ({producible.length})
                            </button>
                        </OeTip>
                    </div>
                ) : null}

                {ex.filter((o) => !paSeen.has(o.id)).length ? (
                    <div className="prodexisting">
                        <div className="muted" style={{marginBottom: 4}}>Weitere Produktionsaufträge:</div>
                        {ex.filter((o) => !paSeen.has(o.id)).map((o) => (
                            <OeTip key={o.id} tip="Öffnet den Produktionsauftrag in weclapp.">
                                <a className="doclink" href={o.webUrl} target="_blank" rel="noopener noreferrer">
                                    {o.number} · {o.articleNumber} ({fmt0(o.quantity)}) · {prodStatusLabel(o.status)} ↗
                                </a>
                            </OeTip>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
